import React, { useEffect, useState } from 'react';
import Swal from 'sweetalert2';
import { useTranslation } from 'react-i18next';
import { useForm, Controller, FormProvider } from 'react-hook-form';

import {
    Box,
    Tab,
    Tabs,
    Select,
    MenuItem,
    TextField,
    FormControl,
    InputLabel,
    Tooltip,
    FormHelperText,
    Paper,
} from '@mui/material';
import { SCUIBreadCrumb } from '../../../../atomic/molecules';
import { SettingsTab } from './Tabs/SettingsTab';
import { HeaderTab } from './Tabs/HeaderTab';
import { BodyTab } from './Tabs/BodyTab';
import { QueryTab } from './Tabs/QueryTab';
import { AuthenticationTab } from './Tabs/AuthenticationTab';

import { useScheduleService } from '../../context/ScheduleServiceContext';
import { SCUIButton } from '../../../../atomic/atoms';

import { ScheduleServiceCommand } from '../../../../services/command';
import { ScheduleServiceQuery } from '../../../../services/query';
import RegexCode from '../../../../types/constants/regex';
import { SC } from './scheduleCreate.classes';

export const ScheduleCreate: React.FC = () => {
    // Constant to identify encrypted/placeholder values (not a password)
    const ENCRYPTED_VALUE_PREFIX = 'ENC:';

    const [activeTab, setActiveTab] = useState(0);
    const [scheduleGroups, setScheduleGroups] = useState([]);

    const methods = useForm({
        defaultValues: {
            name: '',
            jobId: '',
            method: 'GET',
            scheduleUrl: '',
            cron: '* * * * *',
            queueName: 'default',
            hasAuthentication: false,
            isEnabled: false,
            timeZoneId: 'Europe/Istanbul',
            scheduleGroup: '',
            emails: [''],
            headers: [
                {
                    Name: '',
                    Value: '',
                },
            ],
            queryParams: [
                {
                    Name: '',
                    Value: '',
                },
            ],
            address: '',
            contentType: '',
            clientId: '',
            clientSecret: '',
            username: '',
            password: '',
            grantType: '',
            scope: '',
            bodyContent: '',
        },
    });

    const {
        control,
        watch,
        getValues,
        handleSubmit,
        reset,
        formState: { errors },
    } = methods;

    const { t } = useTranslation();
    const {
        isCreating,
        setIsCreating,
        setIsEditing,
        setIsLoading,
        isLoading,
        isEditing,
        selectedJob,
        setSelectedJob,
        userScheduleGroups,
    } = useScheduleService();

    const method = watch('method');
    const hasAuthentication = watch('hasAuthentication');

    const handleTabChange = (_: React.SyntheticEvent, newValue: number) => {
        setActiveTab(newValue);
    };

    const onSubmit = () => {
        handlePostSchedule();
    };

    const handlePostSchedule = () => {
        const values = getValues();

        const {
            headers = [],
            queryParams,
            bodyContent,
            name,
            method,
            scheduleUrl,
            cron,
            queueName,
            hasAuthentication,
            isEnabled,
            timeZoneId,
            emails,
            scheduleGroup,
            jobId,
            password,
            clientSecret,
        } = values;

        if (emails?.length === 0 || !emails || emails?.some((item: any) => item === '')) {
            Swal.fire(t('scheduleService.requiredEmail'), '', 'error');
            return;
        }
        if (queueName && !/^[a-z0-9_-]+$/.test(queueName)) {
            Swal.fire(t('scheduleService.queueNameValid'), '', 'error');
            return;
        }
        if (hasAuthentication) {
            if (selectedJob?.authentication?.password !== ENCRYPTED_VALUE_PREFIX && !password) {
                Swal.fire(t('scheduleService.requiredPassword'), '', 'error');
                return;
            }
            if (selectedJob?.authentication?.clientSecret !== ENCRYPTED_VALUE_PREFIX && !clientSecret) {
                Swal.fire(t('scheduleService.requiredClientSecret'), '', 'error');
                return;
            }
        }

        const headersObj = headers.reduce((acc: Record<string, string>, item: any) => {
            if (item.Name && item.Value) {
                acc[item.Name] = item.Value;
            }
            return acc;
        }, {});

        let payload: Record<string, any> = {
            jobName: name,
            method,
            url: scheduleUrl,
            cron,
            queueName,
            hasAuthentication,
            isEnabled,
            timeout: 3000,
            timeZoneId,
            contentType: 'application/json',
            ownerTeamId: scheduleGroup,
            EmailSettings: { ToEmails: emails },
            headers: headersObj,
            Authentication: {
                address: '',
                contentType: '',
                clientId: '',
                clientSecret: '',
                username: '',
                password: '',
                grantType: '',
                scope: '',
            },
        };

        if (hasAuthentication) {
            payload = {
                ...payload,
                Authentication: {
                    address: values.address,
                    contentType: values.contentType,
                    clientId: values.clientId,
                    clientSecret: values.clientSecret,
                    username: values.username,
                    password: values.password,
                    grantType: values.grantType,
                    scope: values.scope,
                },
            };
        }

        if (method === 'GET') {
            payload.queryParams = queryParams;
        } else {
            payload.bodyContent = bodyContent;
        }

        setIsLoading(true);

        if (isCreating) {
            ScheduleServiceCommand.CreateSchedule(payload)
                .then(() => {
                    Swal.fire({
                        position: 'center',
                        icon: 'success',
                        title: t('general.success'),
                        showConfirmButton: false,
                        timer: 1500,
                    });
                })
                .catch(() => {
                    Swal.fire(t('general.failed'), '', 'error');
                    setIsLoading(false);
                })
                .finally(() => {
                    setIsLoading(false);
                    reset();
                    setSelectedJob({});
                    setIsCreating(false);
                    setIsEditing(false);
                });

            return;
        }

        if (isEditing) {
            payload.jobId = jobId;

            ScheduleServiceCommand.UpdateSchedule(payload)
                .then(() => {
                    Swal.fire({
                        position: 'center',
                        icon: 'success',
                        title: t('general.success'),
                        showConfirmButton: false,
                        timer: 1500,
                    });
                })
                .catch(() => {
                    Swal.fire(t('general.failed'), '', 'error');
                    setIsLoading(false);
                })
                .finally(() => {
                    setIsLoading(false);
                    reset();
                    setSelectedJob({});
                    setIsCreating(false);
                    setIsEditing(false);
                });

            return;
        }
    };

    const tabs = [
        { label: 'Settings', tooltip: t('scheduleService.settings'), disabled: false },
        { label: 'Header', tooltip: t('scheduleService.headers'), disabled: false },
        { label: 'Body', tooltip: t('scheduleService.body'), disabled: method === 'GET' },
        { label: 'Query', tooltip: t('scheduleService.query'), disabled: method !== 'GET' },
        { label: 'Authentication', tooltip: t('scheduleService.authentication'), disabled: !hasAuthentication },
    ];

    useEffect(() => {
        if (method) {
            if (method === 'GET' && activeTab === 2) {
                setActiveTab(3);
                return;
            }
            if (method !== 'GET' && activeTab === 3) {
                setActiveTab(2);
                return;
            }
        }
    }, [method, activeTab]);

    useEffect(() => {
        ScheduleServiceQuery.GetScheduleGroups()
            .then((res: any) => {
                if (res) {
                    const filteredGroups = (res.data || []).filter((item: any) =>
                        (userScheduleGroups || []).includes(item.groupId)
                    );
                    setScheduleGroups(filteredGroups);
                }
            })
            .catch(() => Swal.fire(t('general.failed'), '', 'error'));
    }, []);

    useEffect(() => {
        if (selectedJob && isEditing) {
            reset({
                jobId: selectedJob.jobId,
                name: selectedJob.jobName,
                method: selectedJob.method,
                scheduleUrl: selectedJob.url,
                cron: selectedJob.cron,
                queueName: 'default',
                hasAuthentication: selectedJob.hasAuthentication,
                isEnabled: selectedJob.isEnabled,
                timeZoneId: selectedJob.timeZoneId,
                scheduleGroup: selectedJob.ownerTeamId,
                emails: selectedJob.emailSettings?.toEmails,
                headers: Object.keys(selectedJob.headers || {})?.map((item: any) => {
                    return {
                        Name: item,
                        Value: selectedJob.headers?.[item],
                    };
                }),
                queryParams: selectedJob.queryParams,
                address: selectedJob.authentication?.address,
                contentType: selectedJob.authentication?.contentType,
                clientId: selectedJob.authentication?.clientId,
                clientSecret: '',
                username: selectedJob.authentication?.username,
                password: '',
                grantType: selectedJob.authentication?.grantType,
                scope: selectedJob.authentication?.scope,
                bodyContent: selectedJob.bodyContent,
            });
        }
    }, [selectedJob, reset, isEditing]);

    return (
        <FormProvider {...methods}>
            <form className={SC.form} onSubmit={handleSubmit(onSubmit)}>
                <div className={SC.breadcrumbSection}>
                    <SCUIBreadCrumb
                        onlyTitle={isCreating ? t('scheduleService.addHeader') : t('scheduleService.editHeader')}
                        componentLink={'/schedule-service'}
                        components={[
                            {
                                title: t('scheduleService.header'),
                                link: '/schedule-service',
                                onClick: () => {
                                    setIsCreating(false);
                                    setIsEditing(false);
                                },
                            },
                        ]}
                    />
                </div>
                <Box className={SC.content} sx={{ padding: '16px' }}>
                    <Paper className={SC.headerCard} sx={{ padding: '16px' }}>
                        <Box className={SC.headerRow} sx={{ display: 'flex', flexDirection: 'row' }}>
                            <Box sx={{ marginRight: '16px' }}>
                                <Controller
                                    name="name"
                                    control={control}
                                    rules={{ required: t('scheduleService.requiredName') }}
                                    render={({ field }) => (
                                        <TextField
                                            {...field}
                                            className={SC.fieldName}
                                            label={t('scheduleService.scheduleName')}
                                            variant="outlined"
                                            sx={{ mb: 3, width: '300px' }}
                                            error={!!errors.name}
                                            helperText={errors.name ? String(errors.name.message) : ''}
                                        />
                                    )}
                                />
                            </Box>
                            <Box>
                                <Controller
                                    name="scheduleGroup"
                                    control={control}
                                    rules={{ required: t('scheduleService.requiredGroup') }}
                                    defaultValue={''}
                                    render={({ field }) => (
                                        <FormControl className={SC.fieldGroupControl} fullWidth>
                                            <InputLabel className={SC.fieldGroupLabel} id="schedule-group-label">
                                                {t('scheduleService.scheduleGroup')}
                                            </InputLabel>
                                            <Select
                                                {...field}
                                                labelId="schedule-group-label"
                                                label={t('scheduleService.scheduleGroup')}
                                                error={!!errors.scheduleGroup}
                                                className={SC.fieldGroupSelect}
                                                sx={{ width: '300px' }}
                                            >
                                                <MenuItem className={SC.fieldGroupOption} value="">
                                                    <em>{t('general.select')}</em>
                                                </MenuItem>
                                                {(scheduleGroups || []).map((group: any) => (
                                                    <MenuItem
                                                        className={SC.fieldGroupOption}
                                                        key={group.groupId}
                                                        value={group.groupId}
                                                        data-group-id={group.groupId}
                                                    >
                                                        {group.groupName}
                                                    </MenuItem>
                                                ))}
                                            </Select>
                                            {!!errors.scheduleGroup && (
                                                <FormHelperText sx={{ color: '#d32f2f' }}>
                                                    {errors.scheduleGroup ? String(errors.scheduleGroup.message) : ''}
                                                </FormHelperText>
                                            )}
                                        </FormControl>
                                    )}
                                />
                            </Box>
                        </Box>
                        <Box sx={{ display: 'flex', gap: 2 }}>
                            <FormControl className={SC.methodControl} sx={{ width: '300px' }}>
                                <InputLabel className={SC.methodLabel} id="method-label">
                                    Method
                                </InputLabel>
                                <Controller
                                    name="method"
                                    control={control}
                                    render={({ field }) => (
                                        <Select {...field} className={SC.methodSelect} labelId="method-label" label="Method">
                                            <MenuItem className={SC.methodOption} value="GET">GET</MenuItem>
                                            <MenuItem className={SC.methodOption} value="POST">POST</MenuItem>
                                            <MenuItem className={SC.methodOption} value="PUT">PUT</MenuItem>
                                            <MenuItem className={SC.methodOption} value="PATCH">PATCH</MenuItem>
                                            <MenuItem className={SC.methodOption} value="DELETE">DELETE</MenuItem>
                                        </Select>
                                    )}
                                />
                            </FormControl>
                            <Controller
                                name="scheduleUrl"
                                control={control}
                                rules={{
                                    required: t('scheduleService.requiredUrl'),
                                    pattern: {
                                        value: RegexCode.urlValidation,
                                        message: t('scheduleService.invalidUrl'),
                                    },
                                }}
                                render={({ field }) => (
                                    <TextField
                                        {...field}
                                        className={SC.fieldUrl}
                                        label={t('scheduleService.scheduleUrl')}
                                        variant="outlined"
                                        fullWidth
                                        sx={{ width: '700px' }}
                                        error={!!errors.scheduleUrl}
                                        helperText={errors.scheduleUrl ? String(errors.scheduleUrl.message) : ''}
                                    />
                                )}
                            />
                            <SCUIButton
                                className={SC.saveButton}
                                text={t('general.save')}
                                type="primary"
                                sizeType="fixed"
                                buttonType="submit"
                                disabled={isLoading}
                                size={'m'}
                                style={{ width: '160px', height: '36px', marginLeft: '8px' }}
                            />
                        </Box>
                    </Paper>
                    <Paper className={SC.tabsCard} sx={{ padding: '16px', marginTop: '16px' }}>
                        <Tabs
                            className={SC.tabs}
                            value={activeTab}
                            onChange={handleTabChange}
                            slotProps={{
                                indicator: {
                                    style: { display: 'none' },
                                },
                            }}
                            sx={{
                                '& .MuiTab-root': {
                                    padding: '8px',
                                    minHeight: '40px',
                                    borderTop: '2px solid #e0e0e0',
                                    borderRight: '2px solid #e0e0e0',
                                    borderLeft: '2px solid #e0e0e0',
                                    borderRadius: '4px 4px 0 0',
                                    '&:hover': {
                                        backgroundColor: '#03a9f4',
                                        color: '#ffffff !important',
                                    },
                                },
                                '& .Mui-selected': {
                                    backgroundColor: '#03a9f4',
                                    color: '#ffffff !important',
                                },
                                '& .MuiTabs-flexContainer': {
                                    borderBottom: '2px solid #e0e0e0',
                                },
                            }}
                        >
                            {tabs.map((tab) => (
                                <Tab
                                    className={SC.tab}
                                    key={tab.label}
                                    label={
                                        <Tooltip title={tab.tooltip} arrow>
                                            <span className={SC.tabLabel}>{tab.label}</span>
                                        </Tooltip>
                                    }
                                    sx={{
                                        '&.Mui-disabled': {
                                            backgroundColor: 'rgba(0, 0, 0, 0.1)',
                                        },
                                    }}
                                    disabled={tab.disabled}
                                />
                            ))}
                        </Tabs>
                        <Box className={SC.tabPanel} sx={{ mt: 2, p: 1 }}>
                            {activeTab === 0 && <SettingsTab />}
                            {activeTab === 1 && <HeaderTab />}
                            {activeTab === 2 && <BodyTab />}
                            {activeTab === 3 && <QueryTab />}
                            {activeTab === 4 && <AuthenticationTab />}
                        </Box>
                    </Paper>
                </Box>
            </form>
        </FormProvider>
    );
};
